import { capitalizeFirstLetter } from '@/pages/agents/utils';
import theme from '@/theme';
import { InteractionAggregatesBucket } from '@d-id/types';
import styled from '@emotion/styled';
import { Tooltip } from '@mui/material';
import { format, intervalToDuration } from 'date-fns';
import { InsightsSentiment } from '../../utils';
import { MetricTile } from '../MetricTile';
import { InsightsColumn, TableRow } from '../Table';
import ExpressionlessCircleIcon from './icons/ExpressionlessCircle.svg';
import SadCircleIcon from './icons/SadCircle.svg';
import SmileCircleIcon from './icons/SmileCircle.svg';

const Cell = styled.td<{ grow?: number; minWidth?: number }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    text-align: left;
    color: ${theme.colors.black[700]};
    font-size: 14px;
    font-weight: 400;
    padding: 0;

    ${({ grow }) => grow && `flex: ${grow};`}
    ${({ minWidth }) => minWidth && `min-width: ${minWidth}px;`}
`;

const GroupNameText = styled.span`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;

    overflow: hidden;
    text-overflow: ellipsis;

    :hover {
        text-decoration: underline;
    }
`;

const formatSecondsToMinutes = (seconds: number): string => {
    const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
    const minutes = duration.minutes ?? 0;
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
};

export const getSentiment = (positive: number, negative: number): InsightsSentiment => {
    if (positive) {
        return 'positive';
    }

    if (negative) {
        return 'negative';
    }

    return 'neutral';
};

export const getSentimentIcon = (sentiment: InsightsSentiment): string | undefined => {
    switch (sentiment) {
        case 'positive':
            return SmileCircleIcon;
        case 'negative':
            return SadCircleIcon;
        case 'neutral':
            return ExpressionlessCircleIcon;
    }
};

interface Props {
    columns: InsightsColumn[];
    item: InteractionAggregatesBucket;
    onClick?: () => void;
}

const UserQueryCell = ({ column, item }: { column: InsightsColumn; item: InteractionAggregatesBucket }) => (
    <Cell grow={column.grow} minWidth={column.minWidth}>
        <Tooltip
            title={item.group_name}
            placement="top"
            slotProps={{
                tooltip: { sx: { backgroundColor: theme.colors.secondary[900], padding: '8px', fontSize: '14px' } },
            }}>
            <GroupNameText>{item.group_name}</GroupNameText>
        </Tooltip>
    </Cell>
);

const SatisfactionCell = ({ column, item }: { column: InsightsColumn; item: InteractionAggregatesBucket }) => {
    const sentiment: InsightsSentiment = getSentiment(item.n_conversations_positive, item.n_conversations_negative);

    return (
        <Cell grow={column.grow} minWidth={column.minWidth}>
            <MetricTile
                metrics={[{ value: capitalizeFirstLetter(sentiment), sentiment, icon: getSentimentIcon(sentiment) }]}
                size="default"
            />
        </Cell>
    );
};

const DateCell = ({ column, item }: { column: InsightsColumn; item: InteractionAggregatesBucket }) => (
    <Cell grow={column.grow} minWidth={column.minWidth}>
        {format(new Date(item.start_session || ''), 'MMMM d, h:mm a')}
    </Cell>
);

const DurationCell = ({ column, item }: { column: InsightsColumn; item: InteractionAggregatesBucket }) => (
    <Cell grow={column.grow} minWidth={column.minWidth}>
        {formatSecondsToMinutes(item.session_time || 0)}
    </Cell>
);

const InteractionsCell = ({ column, item }: { column: InsightsColumn; item: InteractionAggregatesBucket }) => (
    <Cell grow={column.grow} minWidth={column.minWidth}>
        {item.n_interactions}
    </Cell>
);

export const ConversationRow = ({ columns, item, onClick }: Props) => {
    return (
        <TableRow onClick={onClick}>
            {columns.map(column => {
                switch (column.label) {
                    case 'User Query':
                        return <UserQueryCell key={column.label} column={column} item={item} />;
                    case 'Satisfaction':
                        return <SatisfactionCell key={column.label} column={column} item={item} />;
                    case 'Date':
                        return <DateCell key={column.label} column={column} item={item} />;
                    case 'Duration':
                        return <DurationCell key={column.label} column={column} item={item} />;
                    case 'Interactions':
                        return <InteractionsCell key={column.label} column={column} item={item} />;
                    default:
                        return null;
                }
            })}
        </TableRow>
    );
};
